import { Injectable } from '@angular/core';
import { CachedHttpService } from './cached-http.service';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { RouterService } from './router.service';
import { HttpClient } from '@angular/common/http';
import { ApiError, CreateError, ReadError, UpdateError } from '../models/error';
import { List } from '../types/list';

@Injectable({
    providedIn: 'root',
})
export class ListService {
    constructor(
        private cachedHttp: CachedHttpService,
        private http: HttpClient,
        private routes: RouterService,
    ) {}

    /**
     * Gets the lists api url with the current session.
     */
    get listsUrl() {
        return this.routes.getApiUrl('lists');
    }

    /**
     * Get the list api url with the current session.
     */
    get listUrl() {
        return this.routes.getApiUrl('list');
    }

    /**
     * Loads a single list by its id.
     * @param id The id of the list.
     */
    get(id: number): Observable<List> {
        const url = `${this.listUrl}/${id}`;
        return this.http.get<List>(url).pipe(
            tap((data) => console.log(data)),
            catchError((error) => this.handleError(error, Errors.Read)),
        );
    }

    /**
     * Loads a list, sets it as active and sets the location url for usage in router links.
     * Loads from the cache by default without its items unless flagged otherwise.
     * @param type The type of the list. (i.e., Personal)
     * @param invalidate Flag to invalidate the cache and load from the api.
     * @param withItems Flag to load the list's items.
     */
    getByType(
        type: string,
        invalidate = false,
        withItems = false,
    ): Observable<List[]> {
        const url = `${this.listsUrl}/${type}/${withItems}`;
        return this.cachedHttp.get<List[]>(url, invalidate).pipe(
            tap((lists) => {
                lists.forEach((list) => {
                    // This used to be in the API but was removed in the rewrite.
                    list.active = true;
                    // This used to get set on the link using ng-route.
                    list.location = this.createLocation(type, list.id);
                });
            }),
            catchError((error) => this.handleError(error, Errors.Read)),
        );
    }

    /**
     * Loads if the user wants to retrieve notifications about the list.
     * @param id The id of the list.
     */
    getListNotifyStatus(id: number) {
        const url = `${this.listsUrl}/instance/${id}/notify`;
        return this.http
            .get<boolean>(url)
            .pipe(
                catchError((error) => this.handleError(error, Errors.Status)),
            );
    }

    /**
     * Removes a list from a bill.
     * @param id The id of the list.
     * @param billId The id of the bill.
     */
    removeListItem(id: number, billId: string): Observable<List> {
        const url = `${this.listsUrl}/${id}/items/${billId}/remove`;
        return this.http
            .post<List>(url, {})
            .pipe(
                catchError((error) => this.handleError(error, Errors.Remove)),
            );
    }

    /**
     * Creates a new, active list.
     * @param type The type of the new list. (i.e., Personal)
     * @param name The name of the new list.
     */
    createNewList(type: string, name: string) {
        const url = `${this.listsUrl}/new`;
        return this.http
            .post(url, { Type: type, Name: name, Active: true })
            .pipe(
                catchError((error) => this.handleError(error, Errors.Create)),
            );
    }

    /**
     * Updates the user's notification status for a list.
     * @param id The id of the list.
     * @param status The new notification status.
     */
    saveNotifyListStatus(id: number, status: boolean): Observable<void> {
        const url = `${this.listsUrl}/instance/${id}/notify/${status}`;
        return this.http
            .post<void>(url, {})
            .pipe(
                catchError((error) => this.handleError(error, Errors.Status)),
            );
    }

    /**
     * Saves the order of a reordered list.
     * @param type The type of the list. (i.e., Personal)
     * @param list The reordered list.
     */
    saveOrder(type: string, list: any[]) {
        const url = `${this.listsUrl}/${type}/update`;
        return this.http
            .post(url, list)
            .pipe(
                catchError((error) => this.handleError(error, Errors.Update)),
            );
    }

    /**
     * Saves the lists associated with a bill.
     * @param billId The id of the bill.
     * @param lists The bill's lists. (could contain multiple list types)
     */
    saveLists(billId: string, lists: List[]) {
        const url = `${this.listsUrl}/item/${billId}/save`;
        return this.http
            .post(url, lists)
            .pipe(
                catchError((error) => this.handleError(error, Errors.Update)),
            );
    }

    /**
     * Loads one type of list for a bill.
     * @param billId The id of the bill.
     * @param type The number representation of the list type.
     */
    getBillList(billId: string, type: number): Observable<List[]> {
        const url = `${this.listsUrl}/bill/${billId}/${type}`;
        return this.http
            .get<List[]>(url)
            .pipe(catchError((error) => this.handleError(error, Errors.Read)));
    }

    private createLocation(type: string, id: number): string {
        const parent = 'parent';
        type = `/${type.toLowerCase() === parent ? `${parent}/` : ''}`;
        return `${this.listsUrl}${type}${id}`;
    }

    private handleError(error: Error, type: Errors) {
        console.error(error);
        let thrown: ApiError;
        switch (type) {
            case Errors.Create:
                thrown = new CreateError(Errors.Create);
                break;
            case Errors.Read:
            case Errors.Status:
                thrown = new ReadError(type);
                break;
            case Errors.Update:
            case Errors.Remove:
                thrown = new UpdateError(Errors.Update);
                break;
            default:
                thrown = new ApiError('An unexpected error occurred');
        }
        return throwError(() => thrown);
    }
}

enum Errors {
    Create = 'Unable to create new list',
    Read = 'Unable to retrieve list',
    Update = 'Unable to update list',
    Status = 'Unable to retrieve or save notify status',
    Remove = 'Unable to remove list item',
}
